// ErrorBoundary de aplicación/ruta (R5.2 / D9).
//
// Un descendiente que lanza durante el render deja la aplicación sin contenido
// (`#root` vacío → pantalla negra). Este boundary conserva un shell visible
// con un fallback RECUPERABLE:
//
//  - el fallback NO depende de la identidad autenticada (es un componente
//    estático) → se puede mostrar incluso sin sesión;
//  - el fallback NO expone stack traces, tokens ni PII;
//  - "Reintentar" remonta el subárbol afectado (bump de `key`), de modo que
//    la ruta puede volver a renderizar sin recargar la aplicación;
//  - "Recargar la página" queda como último recurso explícito (acción
//    secundaria: variante `secondary` del componente compartido + clase
//    `.btn-secondary`, la única secundaria declarada en `@layer components` de
//    `src/index.css` junto a `.btn-primary`);
//  - el registro de diagnóstico es redactado y NO cambia sesión, roles ni
//    caches;
//  - el fallo se ANUNCIA de forma fiable: la región `role="alert"` se monta
//    vacía y su contenido se inserta en un segundo paso (D9).
//
// El límite se monta POR ENCIMA de `<BrowserRouter>` (`src/App.js`): una
// excepción de render en la rama del router también desmontaría el árbol
// React completo y dejaría `#root` vacío.

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, actionButtonClass } from './ui/button';

// D2 (enmienda 2026-10-29): el log de producción lleva SÓLO campos de un enum
// cerrado o de estado. `errorName` se toma de esta lista; cualquier otro
// valor lanzado se registra como 'OtherError' (así el `name` arbitrario del
// payload lanzado nunca llega a la consola).
export const KNOWN_ERROR_NAMES = Object.freeze([
  'Error',
  'TypeError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'EvalError',
  'URIError',
  'AggregateError',
]);

const KNOWN_ERROR_NAME_SET = new Set(KNOWN_ERROR_NAMES);

export function classifyErrorName(error) {
  const name = error && typeof error.name === 'string' ? error.name : '';
  return KNOWN_ERROR_NAME_SET.has(name) ? name : 'OtherError';
}

// D9 (enmienda 2026-10-29): el fallback debe ANUNCIAR el fallo de forma
// fiable. Una región `role="alert"` montada JUNTO a su contenido no la
// anuncian de forma fiable: la región se monta VACÍA y su contenido entra en
// el árbol en un SEGUNDO paso, de modo que la inserción del texto sea la que
// dispara el anuncio. Y al desmontarse el árbol se pierde el foco: el título
// del fallback recibe el foco al montarse (`tabIndex={-1}` + `ref`).
function FallbackTitle({ children }) {
  const headingRef = React.useRef(null);

  React.useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, []);

  return (
    <h1
      ref={headingRef}
      tabIndex={-1}
      data-testid="app-error-boundary-title"
      className="text-lg font-semibold"
    >
      {children}
    </h1>
  );
}

// El contenido del aviso se inserta DESPUÉS del primer commit: la región
// `role="alert"` existe (vacía) antes de que su texto entre en el DOM.
//
// La inserción es POSTERIOR AL PINTADO a propósito (efecto PASIVO, no de
// layout): la región vacía tiene que llegar al árbol de accesibilidad en un
// ciclo de render y su texto en el siguiente. Con un efecto de layout
// (`useLayoutEffect`) región y texto entrarían en la MISMA actualización de
// accesibilidad —una sola por frame—, que es justo el patrón «región y
// contenido juntos» que no se anuncia de forma fiable. El precio es un frame
// con la región vacía; el aviso completo aparece en el siguiente.
function ErrorFallback({ onRetry, onReload }) {
  const [announced, setAnnounced] = React.useState(false);

  React.useEffect(() => {
    setAnnounced(true);
  }, []);

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-4"
      data-testid="app-error-boundary"
    >
      {/* Región viva: se monta vacía; su contenido se inserta después. */}
      <div
        className="w-full max-w-sm space-y-4 text-center"
        role="alert"
        data-testid="app-error-boundary-alert"
      >
        {announced ? (
          <>
            <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
            <FallbackTitle>Algo ha ido mal al mostrar esta sección.</FallbackTitle>
            <p className="mt-2 text-sm text-zinc-400">
              Puedes reintentarlo. Si el problema continúa, recarga la página.
            </p>
            <div className="flex flex-col gap-2">
              {/* D9: las acciones del fallback usan el componente `<Button>`
                  compartido (mismo patrón que el panel de error de
                  `Login.js`) conservando `btn-primary`/`btn-secondary` como
                  `className`: las clases `btn-*` de `index.css` no declaran
                  estado de foco (`focus-visible:ring`) ni de deshabilitado,
                  que el componente compartido sí aporta. `type="button"`
                  explícito: el componente no lo fija por defecto y el botón
                  nunca debe enviar un formulario.

                  La acción SECUNDARIA declara `variant="secondary"` (la
                  variante por defecto aporta `bg-primary`/`text-primary-
                  foreground` en la capa `utilities`, que gana a
                  `@layer components` y hacía que la secundaria se pintara
                  igual que la primaria). `actionButtonClass` (D9) restituye la
                  métrica de `.btn-*` y un anillo de foco que contrasta con el
                  relleno. */}
              <Button
                type="button"
                variant="default"
                onClick={onRetry}
                className={actionButtonClass('btn-primary')}
                data-testid="app-error-retry-btn"
              >
                Reintentar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onReload}
                className={actionButtonClass('btn-secondary')}
                data-testid="app-error-reload-btn"
              >
                Recargar la página
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, resetKey: 0 };
    this.handleRetry = this.handleRetry.bind(this);
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // Diagnóstico redactado (D2/D9): sólo el nombre del error, y sólo si
    // pertenece al enum cerrado. Nunca el stack, el mensaje, el token ni
    // datos personales.
    console.error('[app] error de render', {
      boundary: 'app',
      kind: 'render-error',
      errorName: classifyErrorName(error),
    });
  }

  handleRetry() {
    // El bump de `resetKey` remonta el subárbol afectado (D9).
    this.setState((state) => ({ error: null, resetKey: state.resetKey + 1 }));
  }

  handleReload() {
    try {
      window.location.reload();
    } catch (error) {
      // Entornos sin navegación (tests): la recarga completa es el último
      // recurso y su ausencia no debe romper el fallback.
    }
  }

  render() {
    const { error, resetKey } = this.state;
    const { children } = this.props;

    if (error) {
      return (
        <ErrorFallback onRetry={this.handleRetry} onReload={this.handleReload} />
      );
    }

    return <React.Fragment key={resetKey}>{children}</React.Fragment>;
  }
}

export default ErrorBoundary;
